import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import { FileImageOutlined, TableOutlined, DatabaseOutlined } from "@ant-design/icons";
import { scaleBand, scaleLinear } from "d3-scale";
import { axisBottom, axisLeft } from "d3-axis";
import { select } from "d3-selection";
import { ConditionalTooltip } from "../ConditionalTooltip";

export type HistogramData = {
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    yType: "count" | "frequency";
    isGrouped: boolean;
    bins?: Array<{
        bin: string;
        count: number;
        frequency: number;
        color: string;
    }>;
    series?: Array<{
        name: string;
        color: string;
        bins: Array<{
            bin: string;
            count: number;
            frequency: number;
        }>;
    }>;
};

export const isHistogramData = (columns: any[], dataSource: any[]): boolean => {
    if (!columns || !dataSource || dataSource.length === 0) {
        return false;
    }

    const columnTitles = columns.map(col => (col.title || "").trim());
    const requiredColumns = ["hist_bin", "hist_count", "hist_frequency", "hist_y_type", "hist_color", "hist_title", "hist_x_axis_label", "hist_y_axis_label"];
    const requiredGroupedColumns = [...requiredColumns, "hist_series"];

    const hasAllRegularColumns = requiredColumns.every(reqCol => columnTitles.includes(reqCol));
    const hasAllGroupedColumns = requiredGroupedColumns.every(reqCol => columnTitles.includes(reqCol));

    // Check for exact column count for either regular or grouped
    if (columns.length === 8 && hasAllRegularColumns) return true;
    if (columns.length === 9 && hasAllGroupedColumns) return true;

    return false;
};

export const extractHistogramData = (columns: any[], dataSource: any[]): HistogramData | null => {
    if (!isHistogramData(columns, dataSource)) {
        return null;
    }

    const binCol = columns.find(col => (col.title || "").trim() === "hist_bin");
    const countCol = columns.find(col => (col.title || "").trim() === "hist_count");
    const frequencyCol = columns.find(col => (col.title || "").trim() === "hist_frequency");
    const yTypeCol = columns.find(col => (col.title || "").trim() === "hist_y_type");
    const seriesCol = columns.find(col => (col.title || "").trim() === "hist_series");
    const colorCol = columns.find(col => (col.title || "").trim() === "hist_color");
    const titleCol = columns.find(col => (col.title || "").trim() === "hist_title");
    const xAxisLabelCol = columns.find(col => (col.title || "").trim() === "hist_x_axis_label");
    const yAxisLabelCol = columns.find(col => (col.title || "").trim() === "hist_y_axis_label");

    if (!binCol || !countCol || !frequencyCol || !yTypeCol || !colorCol || !titleCol || !xAxisLabelCol || !yAxisLabelCol) {
        return null;
    }

    const title = String(dataSource[0]?.[titleCol.dataIndex] || "").trim();
    const xAxisLabel = String(dataSource[0]?.[xAxisLabelCol.dataIndex] || "").trim();
    const yAxisLabel = String(dataSource[0]?.[yAxisLabelCol.dataIndex] || "").trim();
    const yType = String(dataSource[0]?.[yTypeCol.dataIndex] || "").trim().toLowerCase();
    const isGrouped = !!seriesCol;

    if (yType !== "count" && yType !== "frequency") {
        return null;
    }

    if (isGrouped && seriesCol) {
        // Grouped histogram
        const seriesMap = new Map<string, { name: string; color: string; bins: Map<string, { count: number; frequency: number }> }>();
        const binSet = new Set<string>();

        dataSource.forEach(row => {
            const bin = String(row[binCol.dataIndex] || "").trim();
            const countStr = String(row[countCol.dataIndex] || "0").replace(/,/g, "");
            const frequencyStr = String(row[frequencyCol.dataIndex] || "0").replace(/,/g, "");
            const seriesName = String(row[seriesCol.dataIndex] || "").trim();
            const color = String(row[colorCol.dataIndex] || "#000000").trim();

            binSet.add(bin);

            if (!seriesMap.has(seriesName)) {
                seriesMap.set(seriesName, {
                    name: seriesName,
                    color: color,
                    bins: new Map()
                });
            }

            seriesMap.get(seriesName)!.bins.set(bin, {
                count: parseFloat(countStr) || 0,
                frequency: parseFloat(frequencyStr) || 0
            });
        });

        const bins = Array.from(binSet).sort();
        const series = Array.from(seriesMap.values()).map(s => ({
            name: s.name,
            color: s.color,
            bins: bins.map(bin => ({
                bin,
                count: s.bins.get(bin)?.count || 0,
                frequency: s.bins.get(bin)?.frequency || 0
            }))
        }));

        return {
            title,
            xAxisLabel: xAxisLabel || undefined,
            yAxisLabel: yAxisLabel || undefined,
            yType: yType as "count" | "frequency",
            isGrouped: true,
            series
        };
    } else {
        // Regular histogram
        const bins = dataSource.map(row => {
            const bin = String(row[binCol.dataIndex] || "").trim();
            const countStr = String(row[countCol.dataIndex] || "0").replace(/,/g, "");
            const frequencyStr = String(row[frequencyCol.dataIndex] || "0").replace(/,/g, "");
            const color = String(row[colorCol.dataIndex] || "#000000").trim();

            return {
                bin,
                count: parseFloat(countStr) || 0,
                frequency: parseFloat(frequencyStr) || 0,
                color
            };
        });

        // Sort bins alphabetically
        bins.sort((a, b) => a.bin.localeCompare(b.bin));

        return {
            title,
            xAxisLabel: xAxisLabel || undefined,
            yAxisLabel: yAxisLabel || undefined,
            yType: yType as "count" | "frequency",
            isGrouped: false,
            bins
        };
    }
};

export function Histogram(props: {
    data: HistogramData;
    darkMode: boolean;
    jobAlias?: string;
    onDownloadCsv?: () => void;
    onDownloadPng?: () => void;
    onAddQuery?: () => void;
}) {
    const { data, darkMode, onDownloadCsv, onDownloadPng, onAddQuery } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
    const [pressedCsv, setPressedCsv] = useState(false);
    const [pressedPng, setPressedPng] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        let currentWidth = 0;
        let currentHeight = 0;
        let isInitialized = false;

        const updateDimensions = (width: number) => {
            if (width > 0) {
                const chartWidth = Math.min(width - 40, 1200);
                const chartHeight = Math.min(chartWidth * 0.625, 600);

                // Only update if dimensions actually changed
                if (chartWidth !== currentWidth || chartHeight !== currentHeight) {
                    currentWidth = chartWidth;
                    currentHeight = chartHeight;
                    setDimensions({ width: chartWidth, height: chartHeight });
                }
            }
        };

        // Initial dimensions - wait for next frame to ensure layout is complete
        requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const initialWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
            if (initialWidth > 0) {
                updateDimensions(initialWidth);
                isInitialized = true;
            }
        });

        // Observe container for size changes with a small debounce
        let resizeTimeout: NodeJS.Timeout;
        const resizeObserver = new ResizeObserver(entries => {
            if (!isInitialized) return; // Ignore resize events until initial dimensions are set
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                for (const entry of entries) {
                    const { width } = entry.contentRect;
                    if (width > 0) {
                        updateDimensions(width);
                    }
                }
            }, 10);
        });

        // Delay observer setup slightly to avoid immediate fire
        const setupTimeout = setTimeout(() => {
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        }, 50);

        return () => {
            clearTimeout(resizeTimeout);
            clearTimeout(setupTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current) return;
        if (!data.isGrouped && (!data.bins || data.bins.length === 0)) return;
        if (data.isGrouped && (!data.series || data.series.length === 0)) return;

        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        const margin = { top: 40, right: data.isGrouped ? 200 : 40, bottom: 120, left: 80 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Set background to transparent
        svg.append("rect")
            .attr("width", dimensions.width)
            .attr("height", dimensions.height)
            .attr("fill", "transparent");

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        if (data.isGrouped && data.series) {
            // Grouped histogram rendering
            const allBins = Array.from(new Set(data.series.flatMap(s => s.bins.map(b => b.bin)))).sort();
            const seriesNames = data.series.map(s => s.name);

            // Calculate max value based on yType
            let maxValue = 0;
            data.series.forEach(series => {
                series.bins.forEach(bin => {
                    const value = data.yType === "count" ? bin.count : bin.frequency;
                    maxValue = Math.max(maxValue, value);
                });
            });

            const yScale = scaleLinear()
                .domain([0, maxValue * 1.1])
                .range([height, 0]);

            // Outer scale for bins
            const xScale = scaleBand()
                .domain(allBins)
                .range([0, width])
                .padding(0.2);

            // Inner scale for series within each bin
            const xSubScale = scaleBand()
                .domain(seriesNames)
                .range([0, xScale.bandwidth()])
                .padding(0.1);

            // Y-axis
            g.append("g")
                .call(axisLeft(yScale))
                .selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");

            // Axis lines
            g.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");

            // Y-axis label
            if (data.yAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(data.yAxisLabel);
            }

            // X-axis with horizontal labels
            const xAxis = g.append("g")
                .attr("transform", `translate(0,${height})`)
                .call(axisBottom(xScale));

            xAxis.selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("transform", null)
                .style("font-size", "12px")
                .style("text-anchor", "middle");

            // Bars for grouped histogram
            data.series.forEach(seriesData => {
                const seriesGroup = g.append("g")
                    .attr("class", `series-${seriesData.name.replace(/\s+/g, '-')}`);

                seriesGroup.selectAll(".bar")
                    .data(seriesData.bins)
                    .enter()
                    .append("rect")
                    .attr("class", "bar")
                    .attr("x", d => {
                        const binX = xScale(d.bin) || 0;
                        const seriesX = xSubScale(seriesData.name) || 0;
                        return binX + seriesX;
                    })
                    .attr("y", d => {
                        const value = data.yType === "count" ? d.count : d.frequency;
                        return yScale(value);
                    })
                    .attr("width", xSubScale.bandwidth())
                    .attr("height", d => {
                        const value = data.yType === "count" ? d.count : d.frequency;
                        return height - yScale(value);
                    })
                    .attr("fill", seriesData.color)
                    .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                    .attr("stroke-width", 1)
                    .on("mouseover", function(this: SVGRectElement, event: MouseEvent, d: { bin: string; count: number; frequency: number }) {
                        select(this)
                            .attr("opacity", 0.8)
                            .attr("stroke-width", 2);

                        const value = data.yType === "count" ? d.count : d.frequency;
                        const valueLabel = data.yType === "count" ? "Count" : "Frequency";
                        const tooltipText = `${seriesData.name} - ${d.bin}: ${valueLabel} = ${value.toLocaleString()}`;
                        const textLength = tooltipText.length * 6;
                        const tooltipWidth = Math.max(textLength + 20, 80);

                        const tooltip = g.append("g")
                            .attr("class", "tooltip")
                            .attr("transform", `translate(${(xScale(d.bin) || 0) + xScale.bandwidth() / 2},${yScale(value) - 15})`);

                        tooltip.append("rect")
                            .attr("x", -tooltipWidth / 2)
                            .attr("y", -12)
                            .attr("width", tooltipWidth)
                            .attr("height", 20)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);

                        tooltip.append("text")
                            .attr("text-anchor", "middle")
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "11px")
                            .attr("y", 2)
                            .text(tooltipText);
                    })
                    .on("mouseout", function(this: SVGRectElement) {
                        select(this)
                            .attr("opacity", 1)
                            .attr("stroke-width", 1);
                        g.selectAll(".tooltip").remove();
                    });
            });

            // Value labels on top of bars for grouped histogram (horizontal)
            data.series.forEach(seriesData => {
                const labelsGroup = g.append("g")
                    .attr("class", `labels-${seriesData.name.replace(/\s+/g, '-')}`);

                labelsGroup.selectAll(".value-label")
                    .data(seriesData.bins)
                    .enter()
                    .append("text")
                    .attr("class", "value-label")
                    .attr("x", d => {
                        const binX = xScale(d.bin) || 0;
                        const seriesX = xSubScale(seriesData.name) || 0;
                        return binX + seriesX + xSubScale.bandwidth() / 2;
                    })
                    .attr("y", d => {
                        const value = data.yType === "count" ? d.count : d.frequency;
                        return yScale(value) - 5;
                    })
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "11px")
                    .attr("font-weight", "500")
                    .text((d) => {
                        const value = data.yType === "count" ? d.count : d.frequency;
                        return value.toLocaleString();
                    });
            });

            // Legend for grouped histograms
            const legend = svg.append("g")
                .attr("class", "legend")
                .attr("transform", `translate(${dimensions.width - margin.right + 20}, ${margin.top + 20})`);

            const legendItems = legend.selectAll(".legend-item")
                .data(data.series)
                .enter()
                .append("g")
                .attr("class", "legend-item")
                .attr("transform", (_d, i: number) => `translate(0, ${i * 25})`);

            legendItems.append("rect")
                .attr("width", 15)
                .attr("height", 15)
                .attr("fill", d => d.color);

            legendItems.append("text")
                .attr("x", 20)
                .attr("y", 12)
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .text(d => d.name);

        } else if (data.bins) {
            // Regular histogram rendering
            const maxValue = Math.max(...data.bins.map(bin => data.yType === "count" ? bin.count : bin.frequency), 0);

            const yScale = scaleLinear()
                .domain([0, maxValue * 1.1])
                .range([height, 0]);

            const xScale = scaleBand()
                .domain(data.bins.map(bin => bin.bin))
                .range([0, width])
                .padding(0.2);

            // Y-axis
            g.append("g")
                .call(axisLeft(yScale))
                .selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");

            // Axis lines
            g.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");

            // Y-axis label
            if (data.yAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(data.yAxisLabel);
            }

            // X-axis with horizontal labels
            const xAxis = g.append("g")
                .attr("transform", `translate(0,${height})`)
                .call(axisBottom(xScale));

            xAxis.selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("transform", null)
                .style("font-size", "12px")
                .style("text-anchor", "middle");

            // Bars
            const bars = g.selectAll(".bar")
                .data(data.bins)
                .enter()
                .append("rect")
                .attr("class", "bar")
                .attr("x", d => xScale(d.bin) || 0)
                .attr("y", d => {
                    const value = data.yType === "count" ? d.count : d.frequency;
                    return yScale(value);
                })
                .attr("width", xScale.bandwidth())
                .attr("height", d => {
                    const value = data.yType === "count" ? d.count : d.frequency;
                    return height - yScale(value);
                })
                .attr("fill", d => d.color)
                .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                .attr("stroke-width", 1)
                .on("mouseover", function(this: SVGRectElement, event: MouseEvent, d: typeof data.bins[0]) {
                    select(this)
                        .attr("opacity", 0.8)
                        .attr("stroke-width", 2);
                })
                .on("mouseout", function(this: SVGRectElement, event: MouseEvent, d: typeof data.bins[0]) {
                    select(this)
                        .attr("opacity", 1)
                        .attr("stroke-width", 1);
                });

            // Value labels on top of bars (horizontal)
            g.selectAll(".value-label")
                .data(data.bins)
                .enter()
                .append("text")
                .attr("class", "value-label")
                .attr("x", d => (xScale(d.bin) || 0) + xScale.bandwidth() / 2)
                .attr("y", d => {
                    const value = data.yType === "count" ? d.count : d.frequency;
                    return yScale(value) - 5;
                })
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .attr("font-weight", "500")
                .text((d) => {
                    const value = data.yType === "count" ? d.count : d.frequency;
                    return value.toLocaleString();
                });
        }

        // X-axis label
        if (data.xAxisLabel) {
            g.append("text")
                .attr("x", width / 2)
                .attr("y", height + margin.bottom - 10)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(data.xAxisLabel);
        }

    }, [data, dimensions, darkMode]);

    const handleDownloadCsv = () => {
        setPressedCsv(true);
        setTimeout(() => setPressedCsv(false), 200);
        if (onDownloadCsv) {
            onDownloadCsv();
        }
    };

    const handleDownloadPng = async () => {
        if (!svgRef.current) return;

        setPressedPng(true);
        setTimeout(() => setPressedPng(false), 200);

        if (onDownloadPng) {
            onDownloadPng();
        }

        try {
            const svg = svgRef.current.cloneNode(true) as SVGElement;

            // Set SVG dimensions
            svg.setAttribute('width', dimensions.width.toString());
            svg.setAttribute('height', dimensions.height.toString());
            svg.setAttribute('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);

            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a data URL with proper namespace
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Scale factor for higher resolution (2x for retina quality)
            const scale = 2;

            img.onload = () => {
                // Set canvas size with scale factor for higher resolution
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                if (ctx) {
                    // Scale the context for high DPI
                    ctx.scale(scale, scale);

                    // Fill background (transparent)
                    ctx.fillStyle = 'transparent';
                    ctx.fillRect(0, 0, img.width, img.height);

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob and download
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', blobUrl);
                            const filename = `${data.title.replace(/[<>:"/\\|?*]/g, '')}.png`;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        }
                    }, 'image/png', 1.0); // Maximum quality
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = () => {
                console.error('Error loading SVG image');
                URL.revokeObjectURL(url);
                notification.error({
                    message: 'Failed to download PNG',
                    description: 'An error occurred while generating the PNG image.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            };

            img.src = url;
        } catch (error) {
            console.error('Error downloading PNG:', error);
            notification.error({
                message: 'Failed to download PNG',
                description: 'An error occurred while generating the PNG image.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    return (
        <div ref={containerRef} style={{ marginBottom: "1em", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: "100%" }}>
            <h3 style={{
                color: darkMode ? "#ffffff" : "#000000",
                marginBottom: "1em",
                textAlign: "center"
            }}>
                {data.title}
            </h3>
            <div style={{ position: "relative", display: "inline-block" }}>
                <svg
                    ref={svgRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    style={{ maxWidth: "100%", overflow: "visible" }}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                />
                <div style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    display: "flex",
                    gap: "0.75em"
                }}>
                    <ConditionalTooltip title="Download histogram as PNG">
                        <button
                            onClick={handleDownloadPng}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedPng ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <FileImageOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Download histogram data as CSV">
                        <button
                            onClick={handleDownloadCsv}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedCsv ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <TableOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Add query">
                        <button
                            onClick={() => {
                                setPressedAddQuery(true);
                                setTimeout(() => setPressedAddQuery(false), 200);
                                onAddQuery?.();
                            }}
                            onMouseEnter={() => setIsAddQueryHovered(true)}
                            onMouseLeave={() => setIsAddQueryHovered(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: isAddQueryHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF"),
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                            }}
                        >
                            <DatabaseOutlined />
                        </button>
                    </ConditionalTooltip>
                </div>
            </div>
        </div>
    );
}

